import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const WORKER_BASE = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? ''

type SubscriptionStatus = {
  plan: 'free' | 'pro'
  subscription: {
    id: string
    amount: number
    expiresAt: number
    createdAt: number
  } | null
}

export function UpgradePage() {
  const { user, token } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<SubscriptionStatus | null>(null)
  const [error, setError] = useState<string | null>(null)

  // 付款結果參數（從 ECPay OrderResultURL 回來）
  const paymentResult = searchParams.get('result') // 'success' | 'fail'

  // 載入訂閱狀態
  useEffect(() => {
    if (!token) return
    fetch(`${WORKER_BASE}/api/subscriptions/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json() as Promise<SubscriptionStatus>)
      .then(setStatus)
      .catch(() => {})
  }, [token, paymentResult])

  async function handleUpgrade() {
    if (!token) {
      navigate('/login')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${WORKER_BASE}/api/subscriptions/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const data = (await res.json()) as { error: string }
        setError(data.error ?? '建立訂單失敗，請稍後再試')
        return
      }

      // 回傳 HTML form，在新視窗打開讓 ECPay 頁面顯示
      const html = await res.text()
      const win = window.open('', '_self')
      if (win) {
        win.document.open()
        win.document.write(html)
        win.document.close()
      }
    } catch {
      setError('網路錯誤，請稍後再試')
    } finally {
      setLoading(false)
    }
  }

  const isPro = status?.plan === 'pro' || user?.plan === 'pro'
  const expiresAt = status?.subscription?.expiresAt
    ? new Date(status.subscription.expiresAt * 1000).toLocaleDateString('zh-TW')
    : null

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <Link to="/" className="block text-center text-2xl font-bold text-indigo-600 mb-8">
          履歷魔法師
        </Link>

        {/* 付款結果提示 */}
        {paymentResult === 'success' && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-4 text-sm text-center">
            付款成功！您的 Pro 方案已啟用。感謝您的支持！
          </div>
        )}
        {paymentResult === 'fail' && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-4 text-sm text-center">
            付款失敗或取消，如有問題請聯繫客服。
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Pro 方案標題 */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 text-white text-center">
            <div className="text-sm font-medium opacity-80 mb-1">履歷魔法師</div>
            <div className="text-3xl font-bold mb-1">Pro 方案</div>
            <div className="text-4xl font-extrabold">
              NT$199{' '}
              <span className="text-lg font-normal opacity-80">/ 月</span>
            </div>
          </div>

          <div className="px-8 py-6">
            {/* 功能列表 */}
            <ul className="space-y-3 mb-8">
              {[
                '無限次 AI 履歷改寫',
                '中英雙格式切換（台灣本土 + 外商英文）',
                'PDF 一鍵匯出',
                '優先處理速度',
                '電子郵件支援',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm text-gray-700">
                  <svg className="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            {/* 免費方案限制說明 */}
            <div className="bg-gray-50 rounded-xl px-4 py-3 mb-6 text-xs text-gray-500 text-center">
              免費方案：每月 3 次改寫
            </div>

            {/* 狀態顯示 */}
            {isPro ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 rounded-full px-4 py-2 text-sm font-medium mb-4">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Pro 方案已啟用
                </div>
                {expiresAt && (
                  <p className="text-xs text-gray-500 mb-4">有效期限：{expiresAt}</p>
                )}
                <Link
                  to="/app"
                  className="block w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-colors text-center"
                >
                  前往使用
                </Link>
              </div>
            ) : (
              <div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
                    {error}
                  </div>
                )}

                {!user ? (
                  <div className="space-y-3">
                    <Link
                      to="/register"
                      className="block w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-semibold text-sm hover:bg-indigo-700 transition-colors text-center"
                    >
                      立即註冊並升級
                    </Link>
                    <Link
                      to="/login"
                      className="block w-full bg-white text-indigo-600 py-3 px-6 rounded-xl font-semibold text-sm border border-indigo-200 hover:bg-indigo-50 transition-colors text-center"
                    >
                      已有帳號？登入後升級
                    </Link>
                  </div>
                ) : (
                  <button
                    onClick={handleUpgrade}
                    disabled={loading}
                    className="w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-semibold text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? '建立訂單中...' : '立即升級 NT$199/月'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 安全付款說明 */}
        <div className="mt-4 text-center text-xs text-gray-400">
          透過綠界科技（ECPay）安全付款 · SSL 加密保護
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">
            ← 返回首頁
          </Link>
        </div>
      </div>
    </div>
  )
}

/** 付款結果頁（ECPay OrderResultURL 回調後的頁面） */
export function UpgradeResultPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // ECPay 回傳的付款結果
  const rtnCode = searchParams.get('RtnCode')
  const success = rtnCode === '1'

  useEffect(() => {
    // 3 秒後跳轉到升級頁
    const timer = setTimeout(() => {
      navigate(`/upgrade?result=${success ? 'success' : 'fail'}`)
    }, 3000)
    return () => clearTimeout(timer)
  }, [success, navigate])

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center">
        {success ? (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">付款成功！</h1>
            <p className="text-gray-500 text-sm">Pro 方案已啟用，正在跳轉...</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">付款未完成</h1>
            <p className="text-gray-500 text-sm">正在跳轉回升級頁面...</p>
          </>
        )}
      </div>
    </div>
  )
}
